import { type StorageAdapter } from "@njbsoft/field-sync";
import {
  createIndexedDbProposalCure,
  createIndexedDbStorage,
  openFieldworkDb,
  type ProposalCureStore,
} from "@njbsoft/field-sync/indexeddb";

// The per-profile IndexedDB handle behind every tester surface: one database
// per signed-in profile, so a shared machine never mixes testers' queued
// regulatory work — and never two live at once, so opening a different profile
// closes and drops the prior entry. This module is the portal's ONLY IndexedDB
// touchpoint; everything else consumes the StorageAdapter contract, plus the
// refused-proposal cure store opened over the same database — a separate
// object because the shared contract carries no counterpart (only this portal
// records violation proposals).

interface OpenHandle {
  db: IDBDatabase;
  adapter: StorageAdapter;
  proposalCure: ProposalCureStore;
}

// Keyed by profile id; concurrent callers share one open through the memoized
// promise. The single-live-database rule keeps this at one entry.
const handles = new Map<string, Promise<OpenHandle>>();

function closeHandle(handle: Promise<OpenHandle>): void {
  void handle
    .then(({ db }) => {
      db.close();
    })
    .catch(() => undefined);
}

function openHandle(profileId: string): Promise<OpenHandle> {
  return openFieldworkDb(profileId).then((db) => ({
    db,
    adapter: createIndexedDbStorage(db),
    proposalCure: createIndexedDbProposalCure(db),
  }));
}

function liveHandle(profileId: string): Promise<OpenHandle> {
  for (const [id, handle] of handles) {
    if (id !== profileId) {
      closeHandle(handle);
      handles.delete(id);
    }
  }
  let handle = handles.get(profileId);
  if (handle === undefined) {
    const opened = openHandle(profileId);
    handles.set(profileId, opened);
    // Evict on rejection so one transient open failure (quota pressure,
    // private-mode restrictions) never bricks the seam for the session — the
    // next mount re-attempts instead of replaying the cached rejection.
    void opened.catch(() => {
      if (handles.get(profileId) === opened) handles.delete(profileId);
    });
    handle = opened;
  }
  return handle;
}

export function getFieldworkStorage(profileId: string): Promise<StorageAdapter> {
  return liveHandle(profileId).then(({ adapter }) => adapter);
}

/**
 * The refused-proposal cure store over `profileId`'s database — the SAME
 * memoized handle `getFieldworkStorage` answers from, so the two never open
 * different databases, and a profile switch or teardown that closes the
 * database drops the store with it.
 */
export function getFieldworkProposalCure(profileId: string): Promise<ProposalCureStore> {
  return liveHandle(profileId).then(({ proposalCure }) => proposalCure);
}

/**
 * Whether a live handle exists for `profileId` — the synchronous probe that
 * lets a session which never opened fieldwork storage tear down without ever
 * awaiting.
 */
export function hasOpenFieldworkStorage(profileId: string): boolean {
  return handles.has(profileId);
}

/**
 * Claims `profileId`'s handle for teardown: the live entry when one exists
 * (removed from the cache before the caller's first await, so nothing else
 * resolves it mid-teardown), else a fresh UNCACHED open — the persisted
 * database outlives any one tab's handle, so the teardown must not depend on
 * this JS context ever having opened it. The caller owns closing the handle.
 */
export function claimFieldworkStorageForTeardown(
  profileId: string,
): Promise<{ db: IDBDatabase; adapter: StorageAdapter }> {
  const live = handles.get(profileId) ?? null;
  if (live !== null) {
    handles.delete(profileId);
    return live;
  }
  return openHandle(profileId);
}

/** Closes and drops every memoized handle (logout / profile switch). */
export function resetFieldworkStorage(): void {
  for (const handle of handles.values()) closeHandle(handle);
  handles.clear();
}
